import { useCallback, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { COLLECTIONS, SUBCOLLECTIONS } from "@/integrations/firebase/collections";
import { logError } from "@/shared/services/ErrorReporting";
import { db } from "@/integrations/firebase/firestore";
import { assertWritable } from "@/hooks/impersonationState";

export interface UseMarkNotificationReadResult {
  markRead: (notificationId: string, isRead: boolean) => Promise<void>;
  loading: boolean;
  error: Error | null;
}

export function useMarkNotificationRead(): UseMarkNotificationReadResult {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const markRead = useCallback(
    async (notificationId: string, isRead: boolean) => {
      assertWritable();
      if (!user || !notificationId) return;

      setLoading(true);
      setError(null);

      try {
        const ref = doc(
          db,
          COLLECTIONS.USERS,
          user.uid,
          SUBCOLLECTIONS.NOTIFICATIONS,
          notificationId,
        );
        await updateDoc(ref, { isRead });
      } catch (err) {
        logError(err as Error);
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [user],
  );

  return { markRead, loading, error };
}
